import { Boolean, Dictionary, Literal, Null, Static, Union } from "runtypes";

import { AdminLearnSteps } from "./learnTypes.js";
import { getNormalEnum } from "./runtypeEnums.js";

export const ContextSetupStepLiteralValues = [
  /**
   * This step is used to track whether we've checked an existing org for completion steps
   */
  Literal("HAS_AUTOCOMPLETED_FOR_EXISTING_ORG"),
  Literal("CONNECT_DATA"),

  Literal("CHOOSE_DEFAULT_DATA_CONNECTION"),
  Literal("ADD_DC_DESCRIPTIONS"),
  Literal("ADD_DATA_DESCRIPTIONS"),

  Literal("ENDORSE_DATA"),
  Literal("ADD_RULES"),
  Literal("ADD_SEMANTIC_MODEL"),
] as const;
export const ContextSetupStepLiteral = Union(...ContextSetupStepLiteralValues);
export type ContextSetupStepLiteral = Static<typeof ContextSetupStepLiteral>;

export const ContextSetupStep = getNormalEnum(ContextSetupStepLiteral);

export const GroupedContextSteps = {
  [ContextSetupStep.CONNECT_DATA]: [
    ContextSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION,
    ContextSetupStep.ADD_DC_DESCRIPTIONS,
    ContextSetupStep.ADD_DATA_DESCRIPTIONS,
  ],
  [ContextSetupStep.ENDORSE_DATA]: [],
  [ContextSetupStep.ADD_RULES]: [],
  [ContextSetupStep.ADD_SEMANTIC_MODEL]: [],
} as const;

export const ContextSetupStepToTrackingStep: Record<
  ContextSetupStepLiteral,
  OnboardingFlowStep | null
> = {
  [ContextSetupStep.CONNECT_DATA]: "CONTEXT_SETUP_CONNECT_DATA",
  [ContextSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION]:
    "CONTEXT_SETUP_CHOOSE_DEFAULT_DATA_CONNECTION",
  [ContextSetupStep.ADD_DC_DESCRIPTIONS]: "CONTEXT_SETUP_ADD_DC_DESCRIPTIONS",
  [ContextSetupStep.ADD_DATA_DESCRIPTIONS]:
    "CONTEXT_SETUP_ADD_DATA_DESCRIPTIONS",
  [ContextSetupStep.ENDORSE_DATA]: "CONTEXT_SETUP_ENDORSE_DATA",
  [ContextSetupStep.ADD_RULES]: "CONTEXT_SETUP_ADD_RULES",
  [ContextSetupStep.ADD_SEMANTIC_MODEL]: "CONTEXT_SETUP_ADD_SEMANTIC_MODEL",
  [ContextSetupStep.HAS_AUTOCOMPLETED_FOR_EXISTING_ORG]: null,
};

export type TopLevelContextStep = keyof typeof GroupedContextSteps;

const WorkspaceSetupStepLiteralValues = [
  ...ContextSetupStepLiteralValues,
  Literal("DISPLAY_NAME"),
  /** @deprecated , but keeping around for historical data */
  Literal("INVITE_TEAMMATES"),
  Literal("WORKSPACE_DATA_CONNECTION"),
  Literal("QUESTIONNAIRE"),
  /** @deprecated — removed from context setup UI but kept for historical data */
  Literal("ADD_DATA_CONNECTION"),
  Literal("FILTER_SCHEMA"),
  Literal("SETUP_SCHEMA_REFRESH"),
  Literal("TEST_AND_OPTIMIZE"),
] as const;
export const WorkspaceSetupLiteral = Union(...WorkspaceSetupStepLiteralValues);

export type WorkspaceSetupStep = Static<typeof WorkspaceSetupLiteral>;
export const WorkspaceSetupStep = getNormalEnum(WorkspaceSetupLiteral);
export const WorkspaceSetupValueType = Union(Boolean, Null).optional();
export const WorkspaceSetupSteps = Dictionary(
  WorkspaceSetupValueType,
  WorkspaceSetupLiteral,
);
export type WorkspaceSetupSteps = Static<typeof WorkspaceSetupSteps>;

export const WorkspaceSetupStepsDefault: Record<
  WorkspaceSetupStep,
  boolean | null
> = {
  [WorkspaceSetupStep.DISPLAY_NAME]: null,
  [WorkspaceSetupStep.CONNECT_DATA]: null,
  [WorkspaceSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION]: null,
  [WorkspaceSetupStep.ADD_DC_DESCRIPTIONS]: null,
  [WorkspaceSetupStep.ADD_DATA_DESCRIPTIONS]: null,
  [WorkspaceSetupStep.ENDORSE_DATA]: null,
  [WorkspaceSetupStep.ADD_RULES]: null,
  [WorkspaceSetupStep.ADD_SEMANTIC_MODEL]: null,
  [WorkspaceSetupStep.HAS_AUTOCOMPLETED_FOR_EXISTING_ORG]: null,
  /** @deprecated — kept for historical data */
  [WorkspaceSetupStep.INVITE_TEAMMATES]: null,
  [WorkspaceSetupStep.WORKSPACE_DATA_CONNECTION]: null,
  [WorkspaceSetupStep.QUESTIONNAIRE]: null,
  [WorkspaceSetupStep.ADD_DATA_CONNECTION]: null,
  [WorkspaceSetupStep.FILTER_SCHEMA]: null,
  [WorkspaceSetupStep.SETUP_SCHEMA_REFRESH]: null,
  [WorkspaceSetupStep.TEST_AND_OPTIMIZE]: null,
};

export const OnboardingFlowStepLiteral = Union(
  ...WorkspaceSetupStepLiteralValues,
  ...AdminLearnSteps.map((step) => Literal(step)),
  Literal("PROJECT_TOUR_STARTED"),
  Literal("PROJECT_TOUR_LOGIC_TAB"),
  Literal("PROJECT_TOUR_DATAFRAME"),
  Literal("PROJECT_TOUR_SHARE"),
  Literal("PROJECT_TOUR_SUPPORT"),
  Literal("APP_BUILDER_TOUR_STARTED"),
  Literal("APP_BUILDER_TOUR_OUTLINE"),
  Literal("APP_BUILDER_TOUR_LAYOUT"),
  Literal("APP_BUILDER_TOUR_SETTINGS"),
  Literal("APP_BUILDER_TOUR_PUBLISH"),
  Literal("DATA_SELECTION_FILE_TOUR_STARTED"),
  Literal("DATA_SELECTION_FILE_TOUR_DATAFRAME"),
  Literal("DATA_SELECTION_FILE_TOUR_ADD_CELLS"),
  Literal("DATA_SELECTION_FILE_TOUR_APP_BUILDER"),
  Literal("DATA_SELECTION_FILE_TOUR_SHARE"),
  Literal("DATA_SELECTION_FILE_TOUR_SUPPORT"),
  Literal("DATA_SELECTION_DC_TOUR_STARTED"),
  Literal("DATA_SELECTION_DC_TOUR_DATAFRAME"),
  Literal("DATA_SELECTION_DC_TOUR_ADD_CELLS"),
  Literal("DATA_SELECTION_DC_TOUR_APP_BUILDER"),
  Literal("DATA_SELECTION_DC_TOUR_SHARE"),
  Literal("DATA_SELECTION_DC_TOUR_SUPPORT"),
  Literal("DATA_SELECTION_DISMISSED_TOUR_STARTED"),
  Literal("DATA_SELECTION_DISMISSED_TOUR_DATAFRAME"),
  Literal("DATA_SELECTION_DISMISSED_TOUR_ADD_CELLS"),
  Literal("DATA_SELECTION_DISMISSED_TOUR_APP_BUILDER"),
  Literal("DATA_SELECTION_DISMISSED_TOUR_SHARE"),
  Literal("DATA_SELECTION_DISMISSED_TOUR_SUPPORT"),
  Literal("PROJECT_TOUR_MAGIC_IN_CELL_BUTTON"),
  Literal("PROJECT_TOUR_CELL_SOURCE"),
  Literal("PROJECT_TOUR_ADD_CELLS"),
  Literal("PROJECT_TOUR_APP_BUILDER"),
  Literal("DATA_SELECTION_MODAL"),
  Literal("CREATE_DATA_CONNECTION"),
  Literal("AI_PROMPT"),
  Literal("AI_DATA_SOURCE"),
  Literal("PROJECT_TOUR_AI_CREATED_DC_STARTED"),
  Literal("PROJECT_TOUR_AI_CREATED_DC_AGENT"),
  Literal("PROJECT_TOUR_AI_CREATED_DC_ADD_CELLS"),
  Literal("PROJECT_TOUR_AI_CREATED_DC_APP_BUILDER"),
  Literal("PROJECT_TOUR_AI_CREATED_DC_SHARE"),
  Literal("PROJECT_TOUR_AI_CREATED_DC_SUPPORT"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC_STARTED"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC_AGENT"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC_ADD_CELLS"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC_APP_BUILDER"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC_SHARE"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC_SUPPORT"),
  Literal("PROMPT_BAR"),
  Literal("ONBOARDING_CHOICE"),
  Literal("THREADS_INTRO_MODAL_PAGE_1"),
  Literal("THREADS_INTRO_MODAL_PAGE_2"),
  Literal("THREADS_INTRO_MODAL_PAGE_3"),

  Literal("CONTEXT_SETUP_CONNECT_DATA"),
  Literal("CONTEXT_SETUP_ADD_DATA_CONNECTION"),
  Literal("CONTEXT_SETUP_CHOOSE_DEFAULT_DATA_CONNECTION"),
  Literal("CONTEXT_SETUP_FILTER_SCHEMA"),
  Literal("CONTEXT_SETUP_SETUP_SCHEMA_REFRESH"),
  Literal("CONTEXT_SETUP_ADD_DATA_DESCRIPTIONS"),
  Literal("CONTEXT_SETUP_ADD_DC_DESCRIPTIONS"),
  Literal("CONTEXT_SETUP_ENDORSE_DATA"),
  Literal("CONTEXT_SETUP_ADD_RULES"),
  Literal("CONTEXT_SETUP_ADD_SEMANTIC_MODEL"),
  Literal("CONTEXT_SETUP_TEST_AND_OPTIMIZE"),
);

export type OnboardingFlowStep = Static<typeof OnboardingFlowStepLiteral>;
export const OnboardingFlowStep = getNormalEnum(OnboardingFlowStepLiteral);

export const OnboardingFlowLiteral = Union(
  Literal("WORKSPACE_SETUP"),
  Literal("USER_SETUP"),
  Literal("PROJECT_TOUR"),
  Literal("APP_BUILDER_TOUR"),
  Literal("DATA_SELECTION_DC_TOUR"),
  Literal("DATA_SELECTION_FILE_TOUR"),
  Literal("DATA_SELECTION_DEMO_TOUR"),
  Literal("DATA_SELECTION_EXISTING_DC_TOUR"),
  Literal("DATA_SELECTION_DISMISSED_TOUR"),
  Literal("PROJECT_TOUR_AI_CREATED_DC"),
  Literal("PROJECT_TOUR_AI_DISMISSED_DC"),
  Literal("DATA_SELECTION"),
  Literal("AI_ONBOARDING"),
  Literal("SETUP_CHECKLIST"),
  Literal("AI_ONBOARDING_V2"),
  Literal("MANAGER_PLUS_THREADS_MODAL"),
  Literal("CONTEXT_SETUP"),
);
export type OnboardingFlow = Static<typeof OnboardingFlowLiteral>;
export const OnboardingFlow = getNormalEnum(OnboardingFlowLiteral);

export const ActionTakenLiteral = Union(
  Literal("SKIPPED"),
  Literal("COMPLETED"),
  Literal("SELECTED_DATA_CONNECTION"),
  Literal("SELECTED_FILE_UPLOAD"),
  Literal("SELECTED_DEMO"),
  Literal("BACK"),
  Literal("SUBMITTED_CSV_PROMPT"),
  Literal("SUBMITTED_DEMO_PROMPT"),
  Literal("SUBMIT_EXISTING_DATA_CONNECTION"),
  Literal("UPLOADED_FILE"),
  Literal("VIEWED_STEP"),
  Literal("VIEWED_POPULAR_PROJECT"),
  Literal("SUBMITTED_PROMPT"),
  Literal("SELECTED_NOTEBOOK"),
  Literal("SELECTED_THREAD"),
);
export type ActionTaken = Static<typeof ActionTakenLiteral>;
export const ActionTaken = getNormalEnum(ActionTakenLiteral);
